import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography, useTheme, alpha } from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SbTextField from '../../../../components/Textfield';
import { BoxType, CopyBoxType, GetRatesContextType } from '../../Type';
import GetRatesContext from '../../../../context/GetRates';
import { ValidationResult } from 'joi';
import { boxSchema } from './Schema';
import Actions from '../Actions';
import updateBoxId from '@Services/updateBoxId';

function CopyBox({ setBoxState, data }: CopyBoxType) {
  const theme = useTheme();
  const { boxes, setBoxes } = React.useContext(GetRatesContext) as GetRatesContextType;
  const [count, setCount] = useState<number>(1);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    const error = validateData();
    if (error) setIsError(true);
    else setIsError(false);
  }, [count]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    if (value) setCount(parseInt(value));
    else setCount(0);
  }

  function handleAction(type: string) {
    const countTemp = type === 'remove' ? Math.max(0, count - 1) : count + 1;
    setCount(countTemp);
  }

  function handleSubmit() {
    if (isError || count <= 0) return;
    setIsError(false);
    const selectedBoxToCopy = structuredClone(data);
    if (selectedBoxToCopy === null) return;
    const boxesTemp = structuredClone(boxes);
    const arr = updateBoxId<BoxType>(boxesTemp, data, count);
    setBoxes([...arr]);
    handleCancel();
  }

  function validateData() {
    const result: ValidationResult = boxSchema.validate({ boxCopyCount: count }, { abortEarly: false });
    const { error } = result;
    return error;
  }

  function handleCancel() {
    setBoxState('details');
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
          Duplicate Box {parseInt(data.id || '0') + 1}
        </Typography>
        <Box
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            px: 1,
            py: 0.25,
            borderRadius: '6px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
          }}
        >
          Box {parseInt(data.id || '0') + 1} of {boxes.length}
        </Box>
      </Box>

      {/* Duplication Controls */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.grey[500], 0.04),
          p: 1.5,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}>
          Number of identical copies to add
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleAction('remove')}
            disabled={count <= 0}
            sx={{ color: theme.palette.primary.main }}
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
          <Box sx={{ width: 80 }}>
            <SbTextField placeholder="1" type="number" value={count} onChange={handleChange} />
          </Box>
          <IconButton size="small" onClick={() => handleAction('add')} sx={{ color: theme.palette.primary.main }}>
            <AddCircleOutlineIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Actions */}
      <Actions onConfirm={handleSubmit} onCancel={handleCancel} disabled={isError || count <= 0} />
    </Box>
  );
}

export default CopyBox;
